/*
 * The GEAR Store (thegearstorepk.com). Original work, not licensed for reuse.
 * See NOTICE.md. This header is a provenance marker — keep it.
 */

/*
 * Shopify strips the comment above when it minifies this file on serve, so the
 * line below is the same marker written as code, which a minifier keeps. Inert
 * by design: one string pushed onto one global, no behaviour, no measurement,
 * nothing sent anywhere. See assets/tgs-home-modules.js for the longer note.
 */
window.TGSOriginal = window.TGSOriginal || [];
window.TGSOriginal.push('thegearstorepk.com/sticky-buy-bar@2026-09-23');

/*
 * Sticky buy bar. See snippets/sticky-buy-bar.liquid for what it is for.
 *
 * The rule that shapes everything here: the bar is a mirror, not a second
 * button. It reads the real ProductSubmitButton and copies its label and its
 * disabled, busy and loading states; a tap on the bar is a click on the real
 * button. Nothing about pre-orders, backorders or the cart is decided here.
 *
 * Three observers, no scroll listener:
 *   IntersectionObserver on the real button      is it on screen?
 *   MutationObserver on the real button, the      what does it say, is it live,
 *     price, the notify-me form and <body>        is a drawer open?
 *   MutationObserver on the form's error box      did the add fail off screen?
 */
(() => {
  if (customElements.get('sticky-buy-bar')) return;

  const VISIBLE_CLASS = 'sticky-buy-bar--visible';
  const PAGE_CLASS = 'tgs-sticky-buy-visible';

  class StickyBuyBar extends HTMLElement {
    connectedCallback() {
      const id = this.dataset.section;
      this.realButton = document.getElementById(`ProductSubmitButton-${id}`);
      this.button = this.querySelector('[data-sticky-buy-button]');
      this.label = this.querySelector('[data-sticky-buy-label]');
      this.price = this.querySelector('[data-sticky-buy-price]');
      this.sourcePrice = document.getElementById(`price-${id}`);
      this.notifyForm = document.getElementById(`ProductBackInStockForm-${id}`);
      this.notifyEmail = document.getElementById(`BackInStockEmail-${id}`);
      this.errorBox = this.realButton?.closest('product-form')?.querySelector('.product-form__error-message-wrapper');
      if (!this.realButton || !this.button || !this.label) return;

      this.mode = 'none';
      this.realOnScreen = true;
      this.lastTap = 0;

      this.onClick = this.onClick.bind(this);
      this.button.addEventListener('click', this.onClick);

      this.mirror = new MutationObserver(() => this.sync());
      this.mirror.observe(this.realButton, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'class'],
        childList: true,
        subtree: true,
        characterData: true,
      });
      if (this.notifyForm) this.mirror.observe(this.notifyForm, { attributes: true, attributeFilter: ['class'] });
      if (this.sourcePrice && this.price) {
        this.mirror.observe(this.sourcePrice, { attributes: true, childList: true, subtree: true, characterData: true });
      }
      // body.overflow-hidden is how the theme marks an open drawer or modal.
      this.mirror.observe(document.body, { attributes: true, attributeFilter: ['class'] });

      if (this.errorBox) {
        this.errorWatch = new MutationObserver(() => this.onErrorChange());
        this.errorWatch.observe(this.errorBox, { attributes: true, attributeFilter: ['hidden'] });
      }

      this.watcher = new IntersectionObserver(
        (entries) => {
          this.realOnScreen = entries[entries.length - 1].isIntersecting;
          this.render();
        },
        { threshold: 0 }
      );
      this.watcher.observe(this.realButton);

      this.sync();
    }

    disconnectedCallback() {
      this.mirror?.disconnect();
      this.errorWatch?.disconnect();
      this.watcher?.disconnect();
      this.button?.removeEventListener('click', this.onClick);
      document.documentElement.classList.remove(PAGE_CLASS);
    }

    /*
     * Work out what the bar should offer, from the real button's state alone.
     *   live button        -> 'buy'    mirror its label, click it
     *   sold out + notify  -> 'notify' offer the notify-me form's own label
     *   sold out, nothing  -> 'none'   stay hidden
     */
    sync() {
      const disabled = this.realButton.hasAttribute('disabled');
      const notifyOpen = !!this.notifyForm && !this.notifyForm.classList.contains('hidden');
      const busy = this.realButton.getAttribute('aria-disabled') === 'true';

      if (!disabled) {
        this.mode = 'buy';
        this.label.textContent = this.realButton.querySelector('span')?.textContent.trim() || this.label.textContent;
      } else if (notifyOpen) {
        this.mode = 'notify';
        const notifyText =
          this.notifyForm.dataset.submitText ||
          this.notifyForm.querySelector('[data-back-in-stock-submit-text]')?.textContent.trim();
        if (notifyText) this.label.textContent = notifyText;
      } else {
        this.mode = 'none';
      }

      this.button.classList.toggle('button--secondary', this.mode === 'notify');
      this.button.classList.toggle('button--primary', this.mode !== 'notify');
      this.button.classList.toggle('loading', this.mode === 'buy' && this.realButton.classList.contains('loading'));
      if (this.mode === 'buy' && busy) {
        this.button.setAttribute('aria-disabled', 'true');
      } else {
        this.button.removeAttribute('aria-disabled');
      }

      if (this.sourcePrice && this.price) {
        // Dawn rewrites #price-<section> on every variant change; take the
        // markup as is and drop the badges, which the bar has no room for.
        this.price.innerHTML = this.sourcePrice.innerHTML;
        this.price.querySelectorAll('.badge').forEach((badge) => badge.remove());
        this.price.querySelector('.price')?.classList.remove('price--large');
        this.price.classList.toggle('hidden', this.sourcePrice.classList.contains('hidden'));
      }

      this.render();
    }

    render() {
      const show = this.mode !== 'none' && !this.realOnScreen && !document.body.classList.contains('overflow-hidden');
      this.classList.toggle(VISIBLE_CLASS, show);
      this.setAttribute('aria-hidden', show ? 'false' : 'true');
      document.documentElement.classList.toggle(PAGE_CLASS, show);
    }

    onClick(event) {
      event.preventDefault();
      if (this.mode === 'buy') {
        // product-form.js owns the re-entry guard; respect it rather than
        // queueing a second submit behind an in-flight one.
        if (this.realButton.getAttribute('aria-disabled') === 'true') return;
        this.lastTap = Date.now();
        this.realButton.click();
      } else if (this.mode === 'notify' && this.notifyForm) {
        this.notifyForm.scrollIntoView({ behavior: this.motionOk() ? 'smooth' : 'auto', block: 'center' });
        this.notifyEmail?.focus({ preventScroll: true });
      }
    }

    // A failed add prints its reason beside the real button, which is off
    // screen whenever this bar is in use. Bring it up, but only for a tap that
    // came from here and only for a moment after it.
    onErrorChange() {
      if (this.errorBox.hasAttribute('hidden')) return;
      if (Date.now() - this.lastTap > 8000) return;
      this.errorBox.scrollIntoView({ behavior: this.motionOk() ? 'smooth' : 'auto', block: 'center' });
    }

    motionOk() {
      return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
  }

  customElements.define('sticky-buy-bar', StickyBuyBar);
})();
